import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaPlus, FaTrash, FaTags, FaSave, FaEdit, FaCheck, FaTimes } from 'react-icons/fa';
import { db } from '../../firebase';
import { doc, getDoc, setDoc, collection, getDocs } from 'firebase/firestore';

const TagManager = () => {
  const [tags, setTags] = useState({
    commonTags: [],
    filterCategories: []
  });
  const [newTag, setNewTag] = useState('');
  const [newCategory, setNewCategory] = useState({ id: '', label: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editCategoryLabel, setEditCategoryLabel] = useState('');
  const [projectCounts, setProjectCounts] = useState({});

  useEffect(() => {
    fetchTags();
    fetchProjectCounts();
  }, []);

  const fetchTags = async () => {
    try {
      setLoading(true);
      const tagsDoc = await getDoc(doc(db, 'settings', 'tags'));
      
      if (tagsDoc.exists()) {
        setTags(tagsDoc.data());
      } else {
        // Initialize with defaults
        const defaultTags = {
          commonTags: ['React', 'Node.js', 'Python', 'TensorFlow', 'MongoDB', 'AWS', 'Docker', 'TypeScript'],
          filterCategories: [
            { id: 'all', label: 'All Projects' },
            { id: 'genai', label: 'Gen AI' },
            { id: 'fullstack', label: 'Full Stack' },
            { id: 'devops', label: 'DevOps & Cloud' }
          ]
        };
        await setDoc(doc(db, 'settings', 'tags'), defaultTags);
        setTags(defaultTags);
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectCounts = async () => {
    try {
      const projectsSnapshot = await getDocs(collection(db, 'projects'));
      const counts = {};
      
      projectsSnapshot.docs.forEach(doc => {
        const category = doc.data().category || 'other';
        counts[category] = (counts[category] || 0) + 1;
      });
      
      // Calculate special counts
      counts['all'] = projectsSnapshot.docs.length;
      
      // Count projects with both fullstack AND genai/ai
      counts['fullstack+genai'] = projectsSnapshot.docs.filter(doc => {
        const data = doc.data();
        const cat = data.category || '';
        const tags = data.tags || [];
        return (cat === 'fullstack' || cat === 'genai' || cat === 'ai') && 
               (tags.some(t => t.toLowerCase().includes('ai') || t.toLowerCase().includes('ml')));
      }).length;
      
      setProjectCounts(counts);
      console.log('📊 Project counts:', counts);
    } catch (error) {
      console.error('Error fetching project counts:', error);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await setDoc(doc(db, 'settings', 'tags'), tags);
      
      // Notify other components that tags have been updated
      window.dispatchEvent(new CustomEvent('tagsUpdated', { detail: tags }));
      
      // Refresh project counts
      await fetchProjectCounts();
      
      alert('Tags saved successfully!');
    } catch (error) {
      console.error('Error saving tags:', error);
      alert('Error saving tags. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const addCommonTag = () => {
    if (newTag && !tags.commonTags.includes(newTag.trim())) {
      setTags({
        ...tags,
        commonTags: [...tags.commonTags, newTag.trim()]
      });
      setNewTag('');
    }
  };

  const removeCommonTag = (tagToRemove) => {
    setTags({
      ...tags,
      commonTags: tags.commonTags.filter(tag => tag !== tagToRemove)
    });
  };

  const addCategory = () => {
    if (newCategory.id && newCategory.label && !tags.filterCategories.find(c => c.id === newCategory.id)) {
      setTags({
        ...tags,
        filterCategories: [...tags.filterCategories, newCategory]
      });
      setNewCategory({ id: '', label: '' });
    }
  };

  const removeCategory = (categoryId) => {
    if (categoryId === 'all') {
      alert('Cannot remove "All Projects" category');
      return;
    }
    setTags({
      ...tags,
      filterCategories: tags.filterCategories.filter(cat => cat.id !== categoryId)
    });
  };

  const startEditCategory = (category) => {
    setEditingCategoryId(category.id);
    setEditCategoryLabel(category.label);
  };

  const saveEditCategory = () => {
    if (!editCategoryLabel.trim()) return;
    
    setTags({
      ...tags,
      filterCategories: tags.filterCategories.map(cat => 
        cat.id === editingCategoryId 
          ? { ...cat, label: editCategoryLabel.trim() }
          : cat
      )
    });
    setEditingCategoryId(null);
    setEditCategoryLabel('');
  };

  const cancelEditCategory = () => {
    setEditingCategoryId(null);
    setEditCategoryLabel('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-white">Tag Manager</h2>
          <p className="text-gray-400 text-sm mt-2">Manage common tags and filter categories</p>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleSave}
          disabled={saving}
          className="flex items-center space-x-2 px-6 py-3 bg-green-600 text-white rounded-lg font-medium shadow-lg hover:bg-green-700 disabled:opacity-50"
        >
          <FaSave />
          <span>{saving ? 'Saving...' : 'Save Changes'}</span>
        </motion.button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Common Tags */}
        <div className="bg-[#1d1836] rounded-2xl p-8 border border-purple-500/20">
          <div className="flex items-center space-x-2 mb-6">
            <FaTags className="text-purple-400 text-xl" />
            <h3 className="text-2xl font-bold text-white">Common Tags</h3>
          </div>
          <p className="text-gray-400 text-sm mb-4">
            Quick-add tags that appear in the project form
          </p>

          {/* Add Tag Input */}
          <div className="flex gap-2 mb-4">
            <input
              type="text"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addCommonTag()}
              placeholder="Add a new tag..."
              className="flex-1 px-4 py-2 bg-[#100d25] border border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-500"
            />
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={addCommonTag}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center space-x-2"
            >
              <FaPlus />
              <span>Add</span>
            </motion.button>
          </div>

          {/* Tags List */}
          <div className="flex flex-wrap gap-2">
            {tags.commonTags.map((tag, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                className="px-3 py-2 bg-purple-500/20 text-purple-300 rounded-full text-sm flex items-center space-x-2"
              >
                <span>{tag}</span>
                <button
                  onClick={() => removeCommonTag(tag)}
                  className="text-red-400 hover:text-red-300"
                >
                  <FaTrash size={12} />
                </button>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Filter Categories */}
        <div className="bg-[#1d1836] rounded-2xl p-8 border border-purple-500/20">
          <div className="flex items-center space-x-2 mb-6">
            <FaTags className="text-blue-400 text-xl" />
            <h3 className="text-2xl font-bold text-white">Filter Categories</h3>
          </div>
          <p className="text-gray-400 text-sm mb-4">
            Categories that appear in the projects filter dropdown
          </p>

          {/* Add Category Input */}
          <div className="space-y-2 mb-4">
            <input
              type="text"
              value={newCategory.id}
              onChange={(e) => setNewCategory({ ...newCategory, id: e.target.value.toLowerCase() })}
              placeholder="Category ID (e.g., 'ai')"
              className="w-full px-4 py-2 bg-[#100d25] border border-blue-500/30 rounded-lg text-white focus:outline-none focus:border-blue-500"
            />
            <input
              type="text"
              value={newCategory.label}
              onChange={(e) => setNewCategory({ ...newCategory, label: e.target.value })}
              placeholder="Category Label (e.g., 'AI & Machine Learning')"
              className="w-full px-4 py-2 bg-[#100d25] border border-blue-500/30 rounded-lg text-white focus:outline-none focus:border-blue-500"
            />
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={addCategory}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center space-x-2"
            >
              <FaPlus />
              <span>Add Category</span>
            </motion.button>
          </div>

          {/* Categories List */}
          <div className="space-y-2">
            {tags.filterCategories.map((category, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg"
              >
                {editingCategoryId === category.id ? (
                  // Edit Mode
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={editCategoryLabel}
                      onChange={(e) => setEditCategoryLabel(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && saveEditCategory()}
                      className="flex-1 px-3 py-1 bg-[#100d25] border border-blue-500/50 rounded text-white focus:outline-none focus:border-blue-500"
                      autoFocus
                    />
                    <button
                      onClick={saveEditCategory}
                      className="text-green-400 hover:text-green-300 p-2"
                      title="Save"
                    >
                      <FaCheck />
                    </button>
                    <button
                      onClick={cancelEditCategory}
                      className="text-gray-400 hover:text-gray-300 p-2"
                      title="Cancel"
                    >
                      <FaTimes />
                    </button>
                  </div>
                ) : (
                  // Display Mode
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium">{category.label}</span>
                        <span className="text-gray-400 text-xs">({category.id})</span>
                        {projectCounts[category.id] !== undefined && (
                          <span className="px-2 py-0.5 bg-purple-500/20 text-purple-300 text-xs rounded-full font-semibold">
                            {projectCounts[category.id]} {projectCounts[category.id] === 1 ? 'project' : 'projects'}
                          </span>
                        )}
                      </div>
                      {/* Special: Show Fullstack + Gen AI count */}
                      {(category.id === 'fullstack' || category.id === 'genai' || category.id === 'ai') && projectCounts['fullstack+genai'] > 0 && (
                        <div className="text-xs text-green-400 mt-1">
                          💡 {projectCounts['fullstack+genai']} Fullstack + Gen AI projects
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => startEditCategory(category)}
                        className="text-blue-400 hover:text-blue-300 p-2"
                        title="Edit label"
                      >
                        <FaEdit />
                      </button>
                      {category.id !== 'all' && (
                        <button
                          onClick={() => removeCategory(category.id)}
                          className="text-red-400 hover:text-red-300 p-2"
                          title="Delete"
                        >
                          <FaTrash />
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-[#1d1836] rounded-2xl p-6 border border-yellow-500/20">
        <h4 className="text-lg font-bold text-yellow-400 mb-2">ℹ️ How to Use</h4>
        <ul className="text-gray-400 text-sm space-y-2">
          <li>• <strong>Common Tags:</strong> These appear as quick-add buttons when creating/editing projects</li>
          <li>• <strong>Filter Categories:</strong> These appear in the project filter dropdown on the Projects page</li>
          <li>• <strong>Edit Category Labels:</strong> Click the edit icon (✏️) to rename a category label (e.g., "AI" → "Gen AI")</li>
          <li>• <strong>Project Counts:</strong> Shows how many projects are in each category</li>
          <li>• <strong>Fullstack + Gen AI:</strong> Automatically counts projects that combine fullstack and AI technologies</li>
          <li>• Make sure category IDs match the category field in your projects (genai, fullstack, devops, etc.)</li>
          <li>• Click "Save Changes" to apply your modifications</li>
        </ul>
      </div>
    </div>
  );
};

export default TagManager;
